// SpeciesOwnerCounts.jsx
// Admin view mirroring scripts/speciesOwnerCounts.mjs to list owner counts per species

import React, { useMemo, useState } from "react";
import db from "../../Firebase";
import { collection, getDocs } from "firebase/firestore";

import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

import "../SpeciesInfoUpload/PlantEditor.css";

const SpeciesOwnerCounts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const loadCounts = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const plantsSnap = await getDocs(collection(db, "Plants"));
      const plantToSpecies = new Map();
      plantsSnap.forEach((docSnap) => {
        const species = docSnap.get("species");
        if (species) {
          plantToSpecies.set(docSnap.id, species);
        }
      });

      const usersSnap = await getDocs(collection(db, "Users"));
      const speciesOwners = new Map();

      usersSnap.forEach((userDoc) => {
        const owned = userDoc.get("ownedPlants");
        if (!Array.isArray(owned) || owned.length === 0) {
          return;
        }
        owned.forEach((plantId) => {
          const species = plantToSpecies.get(plantId);
          if (!species) return;
          if (!speciesOwners.has(species)) {
            speciesOwners.set(species, new Set());
          }
          speciesOwners.get(species).add(userDoc.id);
        });
      });

      const computedResults = Array.from(speciesOwners.entries())
        .map(([species, ownerSet]) => ({
          species,
          ownerCount: ownerSet.size,
        }))
        .sort((a, b) => {
          if (b.ownerCount !== a.ownerCount) {
            return b.ownerCount - a.ownerCount;
          }
          return (a.species || "").localeCompare(b.species || "");
        });

      setResults(computedResults);
      setLastLoadedAt(new Date());
    } catch (err) {
      setError(`Failed to load owner counts: ${err.message}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const totalSpecies = useMemo(() => results.length, [results]);
  const maxOwnerCount = useMemo(() => {
    if (!results.length) return 0;
    return results[0].ownerCount;
  }, [results]);

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Species Owner Counts</div>
      <div className="plantFieldContainer">
        <p style={{ marginBottom: 12 }}>
          Generates the same results as <code>scripts/speciesOwnerCounts.mjs</code>
          , listing how many distinct users own each species.
        </p>
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <input
            className="getPlantButton"
            type="button"
            value={loading ? "Loading..." : "Load Owner Counts"}
            onClick={loadCounts}
            disabled={loading}
          />
          {lastLoadedAt ? (
            <div style={{ fontSize: 13, color: "#666", alignSelf: "center" }}>
              Last loaded {lastLoadedAt.toLocaleString()}
            </div>
          ) : null}
        </div>
        {error ? (
          <div style={{ color: "red", marginBottom: 12 }}>{error}</div>
        ) : null}
        <div style={{ fontSize: 14, color: "#444", marginBottom: 12 }}>
          Found {totalSpecies} species{maxOwnerCount ? ` · Top species has ${maxOwnerCount} owners` : ""}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                <th style={{ padding: "8px 12px" }}>#</th>
                <th style={{ padding: "8px 12px" }}>Species</th>
                <th style={{ padding: "8px 12px" }}>Owner Count</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row, index) => (
                <tr
                  key={row.species || index}
                  style={{
                    borderBottom: "1px solid #f0f0f0",
                    background: index % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <td style={{ padding: "8px 12px", width: 60 }}>{index + 1}</td>
                  <td style={{ padding: "8px 12px", wordBreak: "break-word" }}>
                    {row.species || <em>Unknown species</em>}
                  </td>
                  <td style={{ padding: "8px 12px", width: 140 }}>{row.ownerCount}</td>
                </tr>
              ))}
              {!loading && results.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: "16px 12px", color: "#666" }}>
                    No data loaded yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SpeciesOwnerCounts;
